import React, { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';
import IntakePhotoThumbnails from '../shared/IntakePhotoThumbnails';

const buildBlankQuestion = () => ({
  id: '',
  prompt: '',
  placeholder: '',
  multiline: false,
});

const buildBlankBuilder = () => ({
  id: null,
  name: '',
  description: '',
  questions: [buildBlankQuestion()],
});

export const CustomLeadFormsModule = ({ onBack, currentUser }) => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedFormId, setSelectedFormId] = useState('');

  const [contact, setContact] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    division: 'Custom',
    priority: 'Medium',
    issue_summary: '',
    notes_for_estimator: '',
  });
  const [answers, setAnswers] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [estimatorEmail, setEstimatorEmail] = useState('');
  const [queuedPhotos, setQueuedPhotos] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);

  const [builder, setBuilder] = useState(buildBlankBuilder());
  const [builderSaving, setBuilderSaving] = useState(false);
  const [builderError, setBuilderError] = useState('');

  const isAdmin = currentUser?.rawRole === 'admin';

  const fetchForms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/customer-care/custom-forms`);
      const nextForms = res.data || [];
      setForms(nextForms);
      if (!selectedFormId && nextForms.length > 0) {
        setSelectedFormId(nextForms[0].id);
      }
      if (selectedFormId && !nextForms.find((f) => f.id === selectedFormId)) {
        setSelectedFormId(nextForms[0]?.id || '');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load custom lead forms');
    } finally {
      setLoading(false);
    }
  }, [selectedFormId]);

  React.useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const activeForm = useMemo(() => forms.find((f) => f.id === selectedFormId) || null, [forms, selectedFormId]);

  const setContactField = (key, value) => setContact((prev) => ({ ...prev, [key]: value }));

  const openIntakePhoto = async (intakeId, file) => {
    try {
      const res = await axios.get(`${API}/intakes/${intakeId}/files/${file.id}`, {
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('Error opening custom form photo', err);
      const status = err?.response?.status;
      if (status === 404) {
        alert('Photo file not found. Please re-upload and try again.');
      } else {
        alert('Unable to open photo.');
      }
    }
  };

  const handleSubmit = async (sendToEstimator = false) => {
    if (!activeForm) {
      setSubmitResult({ type: 'error', message: 'Select a form first.' });
      return;
    }

    const answersPayload = (activeForm.questions || []).map((q) => ({
      question_id: q.id,
      question_label: q.prompt,
      answer: (answers[q.id] || '').trim(),
    }));

    if (!contact.name.trim() && !contact.phone.trim() && !contact.email.trim()) {
      setSubmitResult({ type: 'error', message: 'Please provide at least name, phone, or email.' });
      return;
    }

    if (sendToEstimator && !estimatorEmail.trim()) {
      setSubmitResult({ type: 'error', message: 'Estimator email is required to send report.' });
      return;
    }

    setSubmitLoading(true);
    setSubmitResult(null);
    try {
      const res = await axios.post(`${API}/customer-care/custom-forms/${activeForm.id}/submit`, {
        ...contact,
        answers: answersPayload,
      });

      const intakeId = res.data?.intake_id;
      let uploaded = [];

      if (intakeId && queuedPhotos.length > 0) {
        const data = new FormData();
        queuedPhotos.forEach((file) => data.append('files', file));
        setIsUploadingPhotos(true);
        try {
          const uploadRes = await axios.post(`${API}/intakes/${intakeId}/upload-photos`, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          uploaded = uploadRes.data?.uploaded_files || [];
          setUploadedFiles(uploaded);
          // Refresh CRM snapshot with uploaded photo metadata
          await axios.post(`${API}/intakes/${intakeId}/sync-crm`);
        } finally {
          setIsUploadingPhotos(false);
        }
      }

      let sendMessage = '';
      if (sendToEstimator) {
        await axios.post(`${API}/intakes/${intakeId}/send-to-estimator`, {
          estimator_email: estimatorEmail.trim(),
        });
        sendMessage = ' Estimator report sent.';
      }

      const photoMessage = uploaded.length > 0 ? ` ${uploaded.length} photo(s) uploaded.` : '';

      setSubmitResult({
        type: 'success',
        message: `Submission saved to archive and synced to CRM + client records.${photoMessage}${sendMessage}`,
        details: {
          ...res.data,
          uploaded_files_count: uploaded.length,
        },
      });
      setAnswers({});
      setContact((prev) => ({ ...prev, issue_summary: '', notes_for_estimator: '' }));
      setQueuedPhotos([]);
    } catch (err) {
      setSubmitResult({
        type: 'error',
        message: err.response?.data?.detail || 'Failed to submit custom lead form',
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetBuilder = () => {
    setBuilder(buildBlankBuilder());
    setBuilderError('');
  };

  const editBuilderFromForm = (form) => {
    setBuilder({
      id: form.id,
      name: form.name || '',
      description: form.description || '',
      questions: (form.questions || []).map((q) => ({
        id: q.id || '',
        prompt: q.prompt || '',
        placeholder: q.placeholder || '',
        multiline: !!q.multiline,
      })),
    });
    setBuilderError('');
  };

  const updateBuilderQuestion = (index, key, value) => {
    setBuilder((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, [key]: value } : q)),
    }));
  };

  const addBuilderQuestion = () => {
    setBuilder((prev) => ({ ...prev, questions: [...prev.questions, buildBlankQuestion()] }));
  };

  const removeBuilderQuestion = (index) => {
    setBuilder((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const saveBuilder = async () => {
    setBuilderSaving(true);
    setBuilderError('');
    try {
      const payload = {
        name: builder.name,
        description: builder.description,
        questions: builder.questions,
      };
      if (builder.id) {
        await axios.put(`${API}/customer-care/custom-forms/${builder.id}`, payload);
      } else {
        await axios.post(`${API}/customer-care/custom-forms`, payload);
      }
      await fetchForms();
      resetBuilder();
    } catch (err) {
      setBuilderError(err.response?.data?.detail || 'Failed to save custom form');
    } finally {
      setBuilderSaving(false);
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm('Delete this custom lead form? Existing archived submissions will stay.')) return;
    try {
      await axios.delete(`${API}/customer-care/custom-forms/${formId}`);
      await fetchForms();
      if (builder.id === formId) resetBuilder();
    } catch (err) {
      setBuilderError(err.response?.data?.detail || 'Failed to delete form');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white text-gray-900" data-testid="custom-lead-forms-page">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <button onClick={onBack} className="text-blue-600 font-medium hover:text-blue-700" data-testid="custom-lead-forms-back-btn">← Back</button>
            <h1 className="text-3xl font-bold mt-2" data-testid="custom-lead-forms-title">Custom Lead Forms</h1>
            <p className="text-sm text-gray-600 mt-1" data-testid="custom-lead-forms-subtitle">
              Create simple question-based intake forms. Every submission is saved to the intake archive and synced to CRM + clients.
            </p>
          </div>
          <button
            onClick={fetchForms}
            className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            data-testid="custom-lead-forms-refresh-btn"
          >
            Refresh
          </button>
        </div>

        {loading && <div className="text-sm text-gray-500" data-testid="custom-lead-forms-loading">Loading forms…</div>}
        {error && !loading && <div className="text-sm text-red-600" data-testid="custom-lead-forms-error">{error}</div>}

        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="custom-lead-forms-list-panel">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold" data-testid="custom-lead-forms-list-title">Saved Forms</h2>
                <span className="text-xs text-gray-500" data-testid="custom-lead-forms-count">{forms.length} form(s)</span>
              </div>
              {forms.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="custom-lead-forms-empty">No custom forms yet.</p>
              ) : (
                <div className="space-y-2" data-testid="custom-lead-forms-list">
                  {forms.map((form) => {
                    const selected = form.id === selectedFormId;
                    return (
                      <div
                        key={form.id}
                        className={`border rounded-lg p-3 ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}
                        data-testid={`custom-lead-form-card-${form.id}`}
                      >
                        <button
                          onClick={() => {
                            setSelectedFormId(form.id);
                            setSubmitResult(null);
                            setAnswers({});
                            setQueuedPhotos([]);
                            setUploadedFiles([]);
                          }}
                          className="text-left w-full"
                          data-testid={`custom-lead-form-select-${form.id}`}
                        >
                          <p className="font-medium text-sm" data-testid={`custom-lead-form-name-${form.id}`}>{form.name}</p>
                          <p className="text-xs text-gray-500 mt-1" data-testid={`custom-lead-form-questions-${form.id}`}>
                            {(form.questions || []).length} question(s)
                          </p>
                        </button>
                        {isAdmin && (
                          <div className="flex gap-2 mt-2">
                            <button
                              onClick={() => editBuilderFromForm(form)}
                              className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                              data-testid={`custom-lead-form-edit-${form.id}`}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => deleteForm(form.id)}
                              className="text-xs px-2 py-1 border border-red-300 text-red-600 rounded hover:bg-red-50"
                              data-testid={`custom-lead-form-delete-${form.id}`}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5" data-testid="custom-lead-forms-use-panel">
              {!activeForm ? (
                <p className="text-sm text-gray-500" data-testid="custom-lead-forms-no-selected">Select a form to start intake.</p>
              ) : (
                <>
                  <h2 className="text-xl font-semibold" data-testid="custom-lead-active-form-title">{activeForm.name}</h2>
                  {activeForm.description ? (
                    <p className="text-sm text-gray-600 mt-1" data-testid="custom-lead-active-form-description">{activeForm.description}</p>
                  ) : null}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    {[
                      ['name', 'Customer Name'],
                      ['phone', 'Phone'],
                      ['email', 'Email'],
                      ['address', 'Address'],
                      ['city', 'City'],
                      ['province', 'Province'],
                      ['postal_code', 'Postal Code'],
                      ['division', 'Division'],
                    ].map(([key, label]) => (
                      <div key={key}>
                        <label className="text-xs text-gray-600" data-testid={`custom-lead-contact-label-${key}`}>{label}</label>
                        <input
                          value={contact[key] || ''}
                          onChange={(e) => setContactField(key, e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                          data-testid={`custom-lead-contact-input-${key}`}
                        />
                      </div>
                    ))}
                    <div>
                      <label className="text-xs text-gray-600" data-testid="custom-lead-contact-label-priority">Priority</label>
                      <input
                        value={contact.priority}
                        onChange={(e) => setContactField('priority', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid="custom-lead-contact-input-priority"
                      />
                    </div>
                  </div>

                  <div className="mt-5 space-y-3" data-testid="custom-lead-question-answers-panel">
                    {(activeForm.questions || []).map((q) => (
                      <div key={q.id} className="border border-gray-200 rounded-lg p-3" data-testid={`custom-lead-question-${q.id}`}>
                        <label className="text-sm font-medium text-gray-800" data-testid={`custom-lead-question-label-${q.id}`}>{q.prompt}</label>
                        {q.multiline ? (
                          <textarea
                            value={answers[q.id] || ''}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                            placeholder={q.placeholder || 'Type answer...'}
                            rows={4}
                            className="w-full mt-2 border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid={`custom-lead-answer-input-${q.id}`}
                          />
                        ) : (
                          <input
                            value={answers[q.id] || ''}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                            placeholder={q.placeholder || 'Type answer...'}
                            className="w-full mt-2 border border-gray-300 rounded-md px-3 py-2 text-sm"
                            data-testid={`custom-lead-answer-input-${q.id}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600" data-testid="custom-lead-summary-label">Issue Summary (optional)</label>
                      <textarea
                        value={contact.issue_summary}
                        onChange={(e) => setContactField('issue_summary', e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid="custom-lead-summary-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600" data-testid="custom-lead-notes-label">Notes for Estimator (optional)</label>
                      <textarea
                        value={contact.notes_for_estimator}
                        onChange={(e) => setContactField('notes_for_estimator', e.target.value)}
                        rows={3}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid="custom-lead-notes-input"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="text-xs text-gray-600" data-testid="custom-lead-estimator-email-label">Estimator Email (optional unless sending now)</label>
                    <input
                      value={estimatorEmail}
                      onChange={(e) => setEstimatorEmail(e.target.value)}
                      placeholder="estimator@example.com"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="custom-lead-estimator-email-input"
                    />
                  </div>

                  <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="custom-lead-photo-upload-panel">
                    <h3 className="text-sm font-semibold text-gray-900" data-testid="custom-lead-photo-upload-title">Upload Photos</h3>
                    <p className="text-xs text-gray-600 mt-1" data-testid="custom-lead-photo-upload-help">
                      Photos are attached to estimator email and saved in CRM intake snapshot.
                    </p>
                    <div className="mt-2">
                      <label className="inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md cursor-pointer text-sm font-medium" data-testid="custom-lead-photo-upload-btn">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={(event) => {
                            const files = Array.from(event.target.files || []);
                            if (!files.length) return;
                            setQueuedPhotos((prev) => [...prev, ...files]);
                            event.target.value = '';
                          }}
                          className="hidden"
                        />
                        Add Photos
                      </label>
                    </div>

                    {queuedPhotos.length > 0 && (
                      <div className="mt-2" data-testid="custom-lead-queued-photos-list">
                        <p className="text-xs font-medium text-gray-700">Queued photos ({queuedPhotos.length})</p>
                        <div className="mt-1 space-y-1">
                          {queuedPhotos.map((file, index) => (
                            <div key={`${file.name}-${index}`} className="text-xs text-gray-600 flex items-center justify-between gap-2" data-testid={`custom-lead-queued-photo-${index}`}>
                              <span className="truncate">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => setQueuedPhotos((prev) => prev.filter((_, i) => i !== index))}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`custom-lead-queued-photo-remove-${index}`}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {isUploadingPhotos ? (
                      <p className="text-xs text-blue-700 mt-2" data-testid="custom-lead-photo-uploading-message">Uploading photos...</p>
                    ) : null}

                    {uploadedFiles.length > 0 && submitResult?.details?.intake_id ? (
                      <div className="mt-2" data-testid="custom-lead-uploaded-photos-thumbnails">
                        <IntakePhotoThumbnails
                          intakeId={submitResult.details.intake_id}
                          files={uploadedFiles}
                          testIdPrefix="custom-lead-uploaded"
                          onOpen={(file) => openIntakePhoto(submitResult.details.intake_id, file)}
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      onClick={() => handleSubmit(false)}
                      disabled={submitLoading}
                      className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                      data-testid="custom-lead-submit-btn"
                    >
                      {submitLoading ? 'Submitting...' : 'Submit Lead Form'}
                    </button>
                    <button
                      onClick={() => handleSubmit(true)}
                      disabled={submitLoading}
                      className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      data-testid="custom-lead-submit-send-estimator-btn"
                    >
                      {submitLoading ? 'Submitting...' : 'Submit & Send to Estimator'}
                    </button>
                    {submitResult && (
                      <p
                        className={`text-sm ${submitResult.type === 'success' ? 'text-green-700' : 'text-red-600'}`}
                        data-testid="custom-lead-submit-result"
                      >
                        {submitResult.message}
                      </p>
                    )}
                  </div>

                  {submitResult?.details && (
                    <div className="mt-3 text-xs text-gray-700" data-testid="custom-lead-submit-details">
                      Intake ID: {submitResult.details.intake_id || 'N/A'} | CRM Lead: {submitResult.details.crm_lead_id || 'N/A'} | Client: {submitResult.details.sales_client_id || 'N/A'} | Photos: {submitResult.details.uploaded_files_count || 0}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="bg-white border border-gray-200 rounded-xl p-5" data-testid="custom-lead-form-builder-panel">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold" data-testid="custom-lead-form-builder-title">Form Builder (Admin)</h2>
              <button
                onClick={resetBuilder}
                className="text-xs px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
                data-testid="custom-lead-form-builder-new-btn"
              >
                New Form
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600" data-testid="custom-lead-builder-name-label">Form Name</label>
                <input
                  value={builder.name}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  data-testid="custom-lead-builder-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600" data-testid="custom-lead-builder-description-label">Description</label>
                <input
                  value={builder.description}
                  onChange={(e) => setBuilder((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  data-testid="custom-lead-builder-description-input"
                />
              </div>
            </div>

            <div className="mt-4 space-y-3" data-testid="custom-lead-builder-questions-list">
              {builder.questions.map((q, index) => (
                <div key={`${q.id || 'new'}-${index}`} className="border border-gray-200 rounded-lg p-3" data-testid={`custom-lead-builder-question-${index}`}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="md:col-span-2">
                      <label className="text-xs text-gray-600" data-testid={`custom-lead-builder-question-prompt-label-${index}`}>Question</label>
                      <input
                        value={q.prompt}
                        onChange={(e) => updateBuilderQuestion(index, 'prompt', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid={`custom-lead-builder-question-prompt-input-${index}`}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600" data-testid={`custom-lead-builder-question-placeholder-label-${index}`}>Placeholder</label>
                      <input
                        value={q.placeholder}
                        onChange={(e) => updateBuilderQuestion(index, 'placeholder', e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        data-testid={`custom-lead-builder-question-placeholder-input-${index}`}
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <label className="text-xs text-gray-700 flex items-center gap-2" data-testid={`custom-lead-builder-question-multiline-label-${index}`}>
                      <input
                        type="checkbox"
                        checked={q.multiline}
                        onChange={(e) => updateBuilderQuestion(index, 'multiline', e.target.checked)}
                        data-testid={`custom-lead-builder-question-multiline-input-${index}`}
                      />
                      Use paragraph answer box
                    </label>
                    <button
                      onClick={() => removeBuilderQuestion(index)}
                      className="text-xs px-2 py-1 border border-red-300 text-red-600 rounded hover:bg-red-50"
                      data-testid={`custom-lead-builder-question-remove-btn-${index}`}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={addBuilderQuestion}
                className="text-sm px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                data-testid="custom-lead-builder-add-question-btn"
              >
                + Add Question
              </button>
              <button
                onClick={saveBuilder}
                disabled={builderSaving}
                className="text-sm px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                data-testid="custom-lead-builder-save-btn"
              >
                {builderSaving ? 'Saving...' : builder.id ? 'Update Form' : 'Create Form'}
              </button>
            </div>

            {builderError ? (
              <p className="mt-2 text-sm text-red-600" data-testid="custom-lead-builder-error">{builderError}</p>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
